import React, { useState, useCallback } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { SlidersHorizontal, Pencil, Check, Trash2, Plus } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

const OPERATOR_LABELS = {
  equals: "==",
  not_equals: "!=",
  contains: "has",
  not_contains: "!has",
  starts_with: "starts",
  ends_with: "ends",
  greater: ">",
  greater_than_equals: ">=",
  less: "<",
  less_than_equals: "<=",
  is_empty: "empty",
  is_not_empty: "!empty",
};

const VALUE_HIDDEN_OPS = ["is_empty", "is_not_empty"];

const emptyBranch = () => ({
  id: `branch_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
  label: "",
  variable: "",
  operator: "equals",
  value: "",
  dataType: "auto",
});

/* ── Collapsed View ───────────────────────────────────────────── */
const CollapsedView = ({ data, onEdit, branches }) => (
  <div
    className="relative rounded-xl overflow-hidden"
    style={{ backgroundColor: "#FFF7ED", padding: "12px 12px 8px", minWidth: "240px", maxWidth: "340px" }}
  >
    <button
      onClick={(e) => { e.stopPropagation(); onEdit(); }}
      className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
      title="Edit controller"
    >
      <Pencil size={12} className="text-orange-700" />
    </button>

    <div className="bg-white rounded-lg shadow-sm p-3">
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 bg-orange-100 rounded-lg">
          <SlidersHorizontal size={14} className="text-orange-600" />
        </div>
        <span className="text-[12px] font-bold text-orange-900">Controller</span>
      </div>

      {branches.length > 0 ? (
        <div className="space-y-1">
          {branches.map((b) => (
            <div key={b.id} className="text-[9px] text-orange-800 bg-orange-50 px-2 py-1 rounded truncate">
              {b.label || (b.variable ? (
                <span>
                  <span className="font-mono font-semibold">{`{{${b.variable}}}`}</span>
                  {" "}
                  <span className="font-semibold">{OPERATOR_LABELS[b.operator] || b.operator}</span>
                  {!VALUE_HIDDEN_OPS.includes(b.operator) && <> {b.value || "..."}</>}
                </span>
              ) : (
                <span className="italic text-orange-400">empty branch</span>
              ))}
            </div>
          ))}
          {data.hasDefault && (
            <div className="text-[9px] text-gray-500 bg-gray-50 px-2 py-1 rounded font-semibold">Default</div>
          )}
        </div>
      ) : (
        <p className="text-[10px] text-gray-400 italic">No branches defined</p>
      )}
    </div>
  </div>
);

/* ── Expanded Editor ──────────────────────────────────────────── */
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [branches, setBranches] = useState(() =>
    data.branches && data.branches.length > 0
      ? JSON.parse(JSON.stringify(data.branches))
      : [emptyBranch()]
  );
  const [hasDefault, setHasDefault] = useState(data.hasDefault ?? true);
  const [saveError, setSaveError] = useState(null);

  const updateBranch = (idx, field, value) => {
    setBranches((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setSaveError(null);
  };

  const addBranch = () => {
    setBranches((prev) => [...prev, emptyBranch()]);
  };

  const removeBranch = (idx) => {
    if (branches.length <= 1) return;
    setBranches((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (branches.length === 0) { setSaveError("At least one branch is required"); return; }
    for (let i = 0; i < branches.length; i++) {
      if (!branches[i].variable?.trim()) { setSaveError(`Branch ${i + 1}: variable is required`); return; }
    }
    setSaveError(null);
    onSave({ branches, hasDefault });
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#FFF7ED", borderRadius: "12px", padding: "14px", minWidth: "380px", maxWidth: "460px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} className="text-orange-600" />
          <span className="text-[13px] font-bold text-orange-900">Controller</span>
        </div>
        <div className="flex gap-1.5">
          <button onClick={handleSave} className="px-3 py-1.5 text-[11px] font-bold bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-1 shadow-sm">
            <Check size={12} />Save
          </button>
          <button onClick={onCancel} className="px-3 py-1.5 text-[11px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
        </div>
      </div>

      {saveError && <p className="text-[9px] text-red-500 mb-2 font-semibold">{saveError}</p>}

      <NodeEditorTabs nodeType="controller">
      <div className="space-y-0 max-h-[420px] overflow-y-auto pr-1">
        {branches.map((b, bi) => (
          <div key={b.id} className="bg-white rounded-lg shadow-sm p-2.5 mb-1.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold text-orange-700">Branch {bi + 1}</span>
              <button onClick={() => removeBranch(bi)} disabled={branches.length <= 1} className="p-0.5 text-[#667781] hover:text-red-500 disabled:opacity-30"><Trash2 size={10} /></button>
            </div>

            <div className="mb-1.5">
              <label className="text-[9px] font-semibold text-[#667781] uppercase">Label (optional)</label>
              <input
                value={b.label || ""}
                onChange={(e) => updateBranch(bi, "label", e.target.value)}
                placeholder="e.g. Premium User"
                className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none focus:ring-1 focus:ring-orange-400 placeholder:text-[#8696a0] mt-0.5"
              />
            </div>

            <div className="mb-1.5">
              <label className="text-[9px] font-semibold text-[#667781] uppercase">Variable</label>
              <VariableDropdown
                value={b.variable || ""}
                onChange={(name) => updateBranch(bi, "variable", name)}
                flowVariables={flowVariables || []}
                allowCreate
                onCreateVariable={onCreateVariable}
                placeholder="Select variable..."
              />
            </div>

            <div className="flex gap-1.5 mb-1.5">
              <div className="flex-1">
                <label className="text-[9px] font-semibold text-[#667781] uppercase">Operator</label>
                <select value={b.operator || "equals"} onChange={(e) => updateBranch(bi, "operator", e.target.value)} className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none mt-0.5">
                  <option value="equals">Equals</option>
                  <option value="not_equals">Not equals</option>
                  <option value="contains">Contains</option>
                  <option value="not_contains">Does not contain</option>
                  <option value="starts_with">Starts with</option>
                  <option value="ends_with">Ends with</option>
                  <option value="greater">Greater than</option>
                  <option value="greater_than_equals">Greater or equals</option>
                  <option value="less">Less than</option>
                  <option value="less_than_equals">Less or equals</option>
                  <option value="is_empty">Is empty</option>
                  <option value="is_not_empty">Is not empty</option>
                </select>
              </div>
              <div className="w-20">
                <label className="text-[9px] font-semibold text-[#667781] uppercase">Type</label>
                <select value={b.dataType || "auto"} onChange={(e) => updateBranch(bi, "dataType", e.target.value)} className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none mt-0.5">
                  <option value="auto">Auto</option>
                  <option value="number">Number</option>
                  <option value="string">String</option>
                </select>
              </div>
            </div>

            {!VALUE_HIDDEN_OPS.includes(b.operator) && (
              <div>
                <label className="text-[9px] font-semibold text-[#667781] uppercase">Value</label>
                <input value={b.value || ""} onChange={(e) => updateBranch(bi, "value", e.target.value)} placeholder="Comparison value" className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none placeholder:text-[#8696a0] mt-0.5" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-2">
        <button onClick={addBranch} className="text-[10px] text-orange-700 font-bold hover:underline flex items-center gap-1">
          <Plus size={10} />Add Branch
        </button>
        <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
          <input type="checkbox" checked={hasDefault} onChange={(e) => setHasDefault(e.target.checked)} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
          <span className="font-semibold">Default branch</span>
        </label>
      </div>
      </NodeEditorTabs>

      <div className="flex gap-2 mt-3">
        <button onClick={handleSave} className="flex-1 py-2 text-[12px] font-bold bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm">
          <Check size={14} />Save
        </button>
        <button onClick={onCancel} className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
      </div>
    </div>
  );
};

/* ── Main Node ────────────────────────────────────────────────── */
const ControllerNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("controller");
  const branches = data.branches || [];
  const hasDefault = data.hasDefault ?? true;
  const totalHandles = branches.length + (hasDefault ? 1 : 0);

  const handleSave = useCallback((formData) => {
    if (data.onNodeDataChange) data.onNodeDataChange(id, { ...data, ...formData });
    closeEditor();
  }, [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle type="target" position={Position.Left} className="!w-3.5 !h-3.5 !bg-orange-500 !border-2 !border-white !shadow-sm" style={{ left: -7 }} />

      <CollapsedView data={data} onEdit={openEditor} branches={branches} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor data={data} onSave={handleSave} presentation="fullscreen" onCancel={closeEditor} flowVariables={data.flowVariables || []} onCreateVariable={data.onCreateVariable} />
      </FlowNodeEditorShell>

      {/* Dynamic source handles — one per branch */}
      {branches.map((b, index) => (
        <Handle
          key={b.id}
          type="source"
          position={Position.Right}
          id={b.id}
          className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-orange-500"
          style={{
            right: -6,
            top: `${((index + 1) / (totalHandles + 1)) * 100}%`,
          }}
          title={b.label || `Branch ${index + 1}`}
        />
      ))}

      {/* Default handle */}
      {hasDefault && (
        <Handle
          key="default"
          type="source"
          position={Position.Right}
          id="default"
          className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-gray-400"
          style={{
            right: -6,
            top: `${((branches.length + 1) / (totalHandles + 1)) * 100}%`,
          }}
          title="Default"
        />
      )}

      {/* Branch labels */}
      {totalHandles > 0 && (
        <div className="absolute -right-16 top-0 h-full flex flex-col justify-around items-start py-4 pointer-events-none">
          {branches.map((b, i) => (
            <span key={b.id} className="text-[7px] text-orange-700 font-semibold truncate max-w-[60px]" title={b.label || `Branch ${i + 1}`}>
              {b.label || (b.variable ? `{{${b.variable}}}` : `#${i + 1}`)}
            </span>
          ))}
          {hasDefault && (
            <span className="text-[7px] text-gray-500 font-semibold">Default</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ControllerNode;
